<template>
  <div class="card card-navigation rounded-0">
    <div class="card-body position-relative">
      <div class="circle-container">
        <div class="circle circle-one"></div>
        <div class="circle circle-two"></div>
      </div>
      <div class="container">
        <h1 class="card-title text-center text-white">
          <img :src="iconMoney" alt="My Commissions" width="30">
          My Commissions - {{ userFullName }}
        </h1>

        <div class="period-title text-center">
          PERIOD - {{ formattedPeriod(selectedMonth, selectedYear, true, true) }}
        </div>

        <div class="mt-3">
          <div v-if="isLoading" class="loading-spinner text-center">
            <div class="spinner-border" role="status" />
            <small>Loading available months..</small>
          </div>
          <div v-else class="period-month-navigation text-center">
            <div v-for="(periods, year) in groupedPeriods" :key="year">
              <div v-if="Number(year) === Number(selectedYear)">
                <button
                  v-for="period in periods"
                  class="btn btn-sm btn-month mx-1"
                  :class="{'displayed': selectedMonth === period.month}"
                  :key="period.id"
                  @click="viewCommissions(period)"
                >
                  {{ formattedPeriod(period.month, period.year) }}
                  <span v-if="selectedMonth === period.month && period.status === 'active'" class="icon-active"></span>
                  <img v-if="period.status !== 'active'" :src="iconLock" :alt="formattedPeriod(period.month, period.year)">
                </button>
              </div>
            </div>
          </div>

          <div v-if="!isLoading" class="period-navigation text-center mt-3">
            <button
              class="btn btn-arrow"
              data-toggle="tooltip"
              data-placement="left"
              title="Previous Month"
              :disabled="!hasPreviousMonth"
              @click="navigateToPreviousMonth"
            >
              <i class="fa fa-chevron-left"></i>
            </button>
            <button
              class="btn btn-arrow"
              data-toggle="tooltip"
              data-placement="right"
              title="Next Month"
              :disabled="!hasNextMonth"
              @click="navigateToNextMonth"
            >
              <i class="fa fa-chevron-right"></i>
            </button>
          </div>

          <!-- Accept Button -->
          <div v-if="role !== 0 && !isLoading" class="text-center mt-3">
            <button class="btn btn-info btn-reload" :class="{'mr-2': isAcceptable && !isPreviousMonthAcceptable}" @click="$emit('reload', true)">
              Reload
            </button>
            <button v-if="!isPreviousMonthAcceptable" class="btn btn-primary btn-accept" :disabled="!isAcceptable || isLoadingProcess" @click="acceptCommissions(false)">
              <template v-if="!isAcceptable && !isLoadingProcess">
                Commissions Accepted <i class="fa fa-check"></i>
              </template>
              <template v-else-if="isLoadingProcess">
                Processing..
              </template>
              <template v-else>
                Accept Commissions
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPeriod } from "../../../utils/helpers";
import { acceptCommissions, availablePeriods } from "../../../services/api/commissions";

import '../../../../sass/commissions/closed-students-navigation.scss';

export default {
  name: 'ClosedStudentsNavigation',
  props: {
    role: {
      type: Number,
      default: 0,
    },
    userId: {
      type: Number,
      default: 0,
    },
    userName: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: '',
    },
    month: {
      type: Number,
      default: 0
    },
    year: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isLoading: false,
      isLoadingProcess: false,
      hasPreviousMonth: false,
      hasNextMonth: false,
      selectedMonth: this.month,
      selectedYear: this.year,
      availablePeriods: [],
      iconMoney: '/images/icon-money.svg',
      iconLock: '/images/icon-lock.svg',
      userFullName: this.userName,
      groupedPeriods: []
    }
  },
  created() {
    this.fetchAvailablePeriods();
  },
  computed: {
    isAcceptable() {
      const selectedPeriod = this.availablePeriods.find(period => period.month === this.selectedMonth);
      return selectedPeriod && selectedPeriod.status === 'active';
    },
    isPreviousMonthAcceptable() {
      const currentIndex = this.availablePeriods.findIndex(
        period => period.month === this.selectedMonth && period.year === this.selectedYear
      );
      if (currentIndex > 0) {
        const previousPeriod = this.availablePeriods[currentIndex - 1];
        return previousPeriod && previousPeriod.status === 'active';
      }
      return false;
    }
  },
  watch: {
    userId() {
      this.fetchAvailablePeriods();
    }
  },
  methods: {
    formattedPeriod(month, year, mode = false, uppercase = false) {
      return formatPeriod(month, year, mode, uppercase);
    },
    async fetchAvailablePeriods() {
      try {
        this.isLoading = true;

        let response = await availablePeriods(this.userId, this.selectedYear);

        if (response.status === 200) {
          this.availablePeriods = response.data;
          this.userFullName = response.data[0]?.user_name || '';

          if (this.selectedMonth === 1) {
            this.selectedYear -= 1;
            response = await availablePeriods(this.userId, this.selectedYear);

            if (response.status === 200) {
              this.availablePeriods = response.data.concat(this.availablePeriods);
            }
          }

          this.groupPeriodsByYear();
          this.getActivePeriods(this.availablePeriods);
          this.updateNavigationState();
        }
      } catch (error) {
        console.error('Error fetching available periods:', error);
      } finally {
        this.isLoading = false;
      }
    },
    getActivePeriods(availablePeriods) {
      const unlockedPeriods = availablePeriods.filter(period => period.status === 'active');

      if (unlockedPeriods.length > 0) {
        const lastActivePeriod = unlockedPeriods[unlockedPeriods.length - 1];

        this.selectedMonth = this.month && lastActivePeriod ? this.month : lastActivePeriod.month;
        this.selectedYear = lastActivePeriod.year;
      } else {
        this.selectedMonth = this.month;
        this.selectedYear = this.year;
      }

      this.updateNavigationState();
    },
    groupPeriodsByYear() {
      this.groupedPeriods = this.availablePeriods.reduce((acc, period) => {
        if (!acc[period.year]) {
          acc[period.year] = [];
        }
        acc[period.year].push(period);
        return acc;
      }, {});
    },
    async navigateToPreviousMonth() {
      const currentIndex = this.availablePeriods.findIndex(
        period => period.month === this.selectedMonth && period.year === this.selectedYear
      );

      if (currentIndex > 0) {
        await this.viewCommissions(this.availablePeriods[currentIndex - 1]);
      }
    },
    async navigateToNextMonth() {
      const currentIndex = this.availablePeriods.findIndex(
        period => period.month === this.selectedMonth && period.year === this.selectedYear
      );
      if (currentIndex < this.availablePeriods.length - 1) {
        const nextPeriod = this.availablePeriods[currentIndex + 1];
        await this.viewCommissions(nextPeriod);
      } else if (currentIndex === this.availablePeriods.length - 1 && this.selectedYear < new Date().getFullYear()) {
        this.selectedYear += 1;
        await this.fetchAvailablePeriods();
        const firstPeriodOfNextYear = this.availablePeriods[0];
        if (firstPeriodOfNextYear) {
          await this.viewCommissions(firstPeriodOfNextYear);
        }
      }
    },
    updateNavigationState() {
      const currentYear = new Date().getFullYear();
      const currentIndex = this.availablePeriods.findIndex(
        period => period.month === this.selectedMonth && period.year === this.selectedYear
      );

      if (this.selectedMonth === 1) {
        const previousYear = this.availablePeriods.filter(period => period.year === (this.selectedYear - 1));
        this.hasPreviousMonth = previousYear.length > 0;
      } else {
        this.hasPreviousMonth = currentIndex > 0;
      }
      this.hasNextMonth = currentIndex < this.availablePeriods.length - 1 || this.selectedYear < currentYear;

      this.$emit('accepted', this.isAcceptable);
    },
    async viewCommissions(period) {
      this.selectedMonth = period.month;
      this.selectedYear = period.year;

      // Update the URL hash
      window.location.hash = `${this.userId}-${this.selectedYear}-${this.selectedMonth}`;

      this.$emit('selected', this.userId, this.userName, this.position, this.selectedMonth, this.selectedYear);
      this.updateNavigationState();

      await this.fetchAvailablePeriods();
    },
    async acceptCommissions(skip = false) {
      try {
        this.isLoadingProcess = true;
        const payload = {
          skip: skip,
          year: this.selectedYear,
          month:this.selectedMonth
        }

        const response = await acceptCommissions(this.userId, payload);

        if (response.status === 201) {
          const updatedPeriod = this.availablePeriods.find(period =>
            period.month === this.selectedMonth && period.year === this.selectedYear
          );

          if (updatedPeriod) {
            updatedPeriod.status = 'paid';
          }

          this.updateNavigationState();

          new Noty({
            type: response.data.status,
            text: response.data.message,
          }).show();
        } else if (response.status === 200 && response.data.students.length > 0) {
          this.$emit('viewPendingApproval', response.data, true);
        }

        this.updateNavigationState();
      } catch (error) {
        console.error('Error processing the commissions:', error);

        new Noty({
          type: "error",
          text: 'Failed to process the commissions. Please try again later.',
        }).show();
      } finally {
        this.isLoadingProcess = false;
      }
    }
  }
}
</script>